import { qfRestGet } from './qfClient';
import { todayEt } from '../utils/marketTime';
import { normalizeOptionsTicker } from '../utils/tickerDisplay';

/**
 * Gamma exposure for the Ticker Explorer's Gamma tab, from QuantForge.
 *
 * Four panels are served from three calls here: per-strike GEX for one expiry
 * and for all expiries, per-expiry GEX, and the expiry list that lets the
 * first of those pick a real date.
 *
 * ### Deliberately separate from `gexService`
 *
 * `gexService` feeds the dashboard's GEX widget, which switches between four
 * metrics — gex, delta, charm and vanna — and its rows carry all four pairs.
 * These routes return the GEX pair only, because the Gamma tab hardcodes
 * `metric: 'gex'`.
 *
 * Repointing `gexService` at them would therefore leave the dashboard's other
 * three metrics reading `undefined`, which renders as an empty chart rather
 * than an error. Keep the two apart until QF serves the full greek set.
 *
 * ### Sign convention — load-bearing
 *
 * ⚠️ `call_gex` is non-negative and `put_gex` is **already negative**, so net
 * is `call + put`, not `call - put`. This matches what the panels computed
 * against the previous feed, so the arithmetic downstream is unchanged.
 *
 * A provider that flipped `put_gex` positive would invert every net figure on
 * the page and produce no error at all — the numbers would simply be wrong.
 * `netGex` below is the one place that arithmetic lives, so a future feed with
 * the opposite convention is a one-line change here rather than a hunt.
 *
 * ### Wire types
 *
 * ⚠️ Numeric fields may arrive as strings. The interfaces below declare the
 * union honestly rather than lying about it — coerce with `num()` at the point
 * of use. This bit us on the previous feed, where the declared types said
 * `number`, every field was a string, and calling a number method typechecked
 * cleanly and threw at runtime.
 */

/** Coerce a wire number that may have arrived as a string. Non-finite → 0. */
export function num(v: number | string | null | undefined): number {
  const n = typeof v === 'number' ? v : Number(v);
  return Number.isFinite(n) ? n : 0;
}

/**
 * Net exposure from a call/put pair.
 *
 * ⚠️ Addition, not subtraction — puts arrive negative. See the module note.
 */
export function netGex(row: { call_gex: number | string; put_gex: number | string }): number {
  return num(row.call_gex) + num(row.put_gex);
}

/** ⚠️ Wire shape. Numeric fields may be strings. */
export interface GammaStrikeRow {
  strike: number | string;
  /** ≥ 0. */
  call_gex: number | string;
  /** ≤ 0 — already negated. */
  put_gex: number | string;
}

/** ⚠️ Wire shape. Numeric fields may be strings. */
export interface GammaByStrikeResponse {
  symbol: string;
  /** ISO timestamp of the snapshot. Drives the cache window — see `cachePolicy`. */
  as_of: string;
  spot: number | string;
  /**
   * The expiry these rows cover, `YYYY-MM-DD`, or null for an all-expiries
   * aggregate.
   *
   * ⚠️ **Label the panel from this, never from a locally computed date.** When
   * `exp=next` is requested the server resolves which expiry that is, and it is
   * routinely not the date a calendar would guess: NVDA lists Mon/Wed/Fri plus
   * monthlies, plenty of names are weekly only.
   */
  expiry: string | null;
  rows: GammaStrikeRow[];
}

/**
 * How an expiry settles. Comes from the option root, never from the date.
 *
 * ⚠️ **This is the field that keeps two real products apart.** SPX(AM) and
 * SPXW(PM) expire on the same third Friday every month. QuantForge measured
 * 2026-08-21 carrying SPX net_gex 3,258,872 against SPXW's 13,374 — collapsing
 * those onto the date alone reports a single "Aug 21" at 244x the PM book,
 * true of neither product. Non-monthly dates are PM-only and yield one row, so
 * the split appears exactly where it is real.
 */
export type Settlement = 'AM' | 'PM';

/** ⚠️ Wire shape — one tradable expiry, for one settlement regime. */
export interface ExpirationRow {
  /**
   * `YYYY-MM-DD`, a bare ISO date.
   *
   * ⚠️ **Not unique on its own.** Key on `(expiry, settlement)` — see the note
   * on `Settlement`. Deduplicating on the date alone silently drops a real
   * chain.
   */
  expiry: string;
  /** The option root this row came from, e.g. `SPXW`. */
  root: string;
  settlement: Settlement;
  /** `monthly` | `weekly`. Display label only — do not key on it. */
  cycle: string;
}

/** ⚠️ Wire shape. Numeric fields may be strings. */
export interface GammaExpiryRow extends ExpirationRow {
  /** ≥ 0. */
  call_gex: number | string;
  /** ≤ 0 — already negated. */
  put_gex: number | string;
  net_gex: number | string;
}

/**
 * ⚠️ Wire shape.
 *
 * ⚠️ The list key is `expiries`, and `/v1/expirations` uses `expirations`.
 * They differ. Both were verified against QuantForge's response models rather
 * than assumed — reading the wrong key yields `[]`, which renders as "no data"
 * and is indistinguishable from an empty chain.
 */
export interface GammaByExpiryResponse {
  symbol: string;
  /** The roots that went into this response, e.g. `["SPX", "SPXW"]`. */
  roots: string[];
  calc_type: string;
  date: string;
  expiries: GammaExpiryRow[];
}

/** ⚠️ Wire shape. See the key warning on `GammaByExpiryResponse`. */
export interface ExpirationsResponse {
  symbol: string;
  roots: string[];
  expirations: ExpirationRow[];
}

/** Which expiries a by-strike request covers. */
export type ExpScope = 'next' | 'all';

/**
 * Normalises a ticker to the OPTIONS ROOT QuantForge expects.
 *
 * ⚠️ Uppercase before sending. At least one QF route checks ticker scope
 * against the JWT's claims *before* upper-casing, so a lower-case symbol can
 * 403 on a ticker the caller is entitled to.
 *
 * ⚠️ **The SPX mapping is not optional here.** The Ticker Explorer passes the
 * raw symbol a user typed, uppercased and nothing more — it never routes
 * through `normalizeOptionsTicker` on the way in. So a reader who types "SPX",
 * meaning the weeklies as our whole UI defines it, would otherwise reach QF as
 * the literal `SPX`, which on their side is the AM-settled **monthly**. That is
 * a different chain rendering under the heading the reader asked for, with no
 * error anywhere. `normalizeOptionsTicker` maps SPX→SPXW and SPXAM→SPX.
 *
 * ⚠️ **Price data must NOT use this.** OHLC for every member of the SPX family
 * collapses to the `SPX` index — see `underlyingPriceTicker`. Keying candles
 * off `SPXW` returns nothing. The two functions disagree for the same input on
 * purpose: gamma wants the options root, the price ladder wants the underlying.
 */
function qfSymbol(ticker: string): string {
  return normalizeOptionsTicker(ticker.trim().toUpperCase());
}

/**
 * Per-strike gamma exposure, for one expiry or aggregated across all of them.
 *
 * `exp: 'next'` asks the server to resolve the soonest listed expiry — read the
 * resolved date back off `expiry` rather than assuming it.
 */
export async function fetchGammaByStrike(
  ticker: string,
  exp: ExpScope
): Promise<GammaByStrikeResponse> {
  return qfRestGet<GammaByStrikeResponse>('/v1/gamma/by-strike', {
    symbol: qfSymbol(ticker),
    exp,
  });
}

/**
 * Gamma exposure aggregated per expiration, one row per settlement regime.
 *
 * ⚠️ Route is `/v1/gex-by-expiry`. We previously called `/v1/gamma/by-expiry`,
 * a name from our own proposal that was never built — it 404'd on every
 * request from the day it shipped.
 */
export async function fetchGammaByExpiry(ticker: string): Promise<GammaByExpiryResponse> {
  return qfRestGet<GammaByExpiryResponse>('/v1/gex-by-expiry', {
    symbol: qfSymbol(ticker),
  });
}

/**
 * Every listed expiry for a symbol, soonest first.
 *
 * Serves two purposes, which is why we want the whole list rather than just a
 * resolved "next": the dashboard widget's dropdown lets a reader pick any
 * expiry, and the Gamma tab's pinned pane takes `[0]` as "next". Asking the
 * server to resolve "next" alone would cover the second and leave the first
 * with nothing to populate from.
 *
 * ⚠️ **Throws rather than returning an empty array.** An earlier version of
 * this call swallowed failures to `[]` because it fed an optional dropdown the
 * user could bypass with a date field. The Gamma tab hides that field, so an
 * empty list there is indistinguishable from a symbol with no listed options,
 * and the panel silently fell back to showing all-expiries data under a
 * "Next Expiry" heading. Let the error reach React Query.
 */
export async function fetchExpirations(ticker: string): Promise<ExpirationsResponse> {
  return qfRestGet<ExpirationsResponse>('/v1/expirations', {
    symbol: qfSymbol(ticker),
  });
}

/**
 * Whole days from today to an expiry. 0 is a 0DTE contract.
 *
 * Compared in ET, not the viewer's zone: a reader in Europe is past midnight
 * while the US session is still open, and a local-midnight comparison would
 * call the live 0DTE chain expired.
 */
export function daysToExpiry(expiry: string): number {
  const from = new Date(`${todayEt()}T00:00:00Z`).getTime();
  const to = new Date(`${expiry}T00:00:00Z`).getTime();
  return Math.round((to - from) / 86_400_000);
}
